import React from 'react';
import { Flex, Text, IconButton, Image, Box, Tr, Td, Icon, Checkbox } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

export interface DraggableTrack {
  id: string;
  trackId: string;
  contentId: string;
  title: string | null;
  artist: string | null;
  durationSeconds: number | null;
  position: number;
  thumbnailUrl: string | null;
  genre: string | null;
  tags: string[];
}

interface DraggableTrackItemProps {
  track: DraggableTrack;
  onRemove: (trackId: string) => void;
  /** If true, renders as a drag overlay ghost */
  isOverlay?: boolean;
  /** ID of the track currently playing in the floating player */
  playingTrackId?: string;
  /** Callback to toggle play in the floating player */
  onPlayToggle?: (track: DraggableTrack) => void;
  /** Edit this single track's metadata (opens the bulk-edit modal scoped to one track) */
  onEdit?: (track: DraggableTrack) => void;
  /** When provided, a leading checkbox column is rendered for bulk selection. */
  selectable?: boolean;
  /** Whether this row's track is currently selected (keyed by contentId). */
  isSelected?: boolean;
  /** Toggle this track's selection. */
  onToggleSelect?: (track: DraggableTrack) => void;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function DraggableTrackItem({ track, onRemove, isOverlay = false, playingTrackId, onPlayToggle, onEdit, selectable = false, isSelected = false, onToggleSelect }: DraggableTrackItemProps) {
  const { t } = useTranslation();
  const isPlayingInFloating = playingTrackId === track.contentId;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: track.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    ...(isOverlay ? { boxShadow: '0 4px 12px rgba(0,0,0,0.15)', backgroundColor: '#fff', borderRadius: 8 } : {}),
  };

  return (
    <Tr
      ref={setNodeRef}
      style={style}
      data-testid={`draggable-track-item-${track.id}`}
      bg={isSelected ? 'blue.50' : undefined}
      _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
    >
      {/* Selection checkbox (bulk manage) */}
      {selectable && (
        <Td w="40px" px={2} textAlign="center">
          {!isOverlay && (
            <Checkbox
              isChecked={isSelected}
              onChange={() => onToggleSelect?.(track)}
              data-testid={`select-track-${track.contentId}`}
              aria-label={track.title ?? track.contentId}
            />
          )}
        </Td>
      )}

      {/* Position number */}
      <Td w="40px" px={2} textAlign="center" color="gray.500" fontSize="sm">
        {track.position}
      </Td>

      {/* Track: thumbnail + title + artist */}
      <Td>
        <Flex align="center" gap={3}>
          <Box
            w="40px"
            h="40px"
            flexShrink={0}
            borderRadius="8px"
            overflow="hidden"
            bg="gray.100"
            position="relative"
            role="group"
            cursor={onPlayToggle ? 'pointer' : undefined}
            onClick={onPlayToggle ? () => onPlayToggle(track) : undefined}
            data-testid={`playlist-play-button-${track.id}`}
          >
            {track.thumbnailUrl ? (
              <Image src={track.thumbnailUrl} alt="" w="100%" h="100%" objectFit="cover" />
            ) : (
              <Flex w="100%" h="100%" bg="gray.200" align="center" justify="center">
                <Icon viewBox="0 0 24 24" w={4} h={4} color="gray.400">
                  <path fill="currentColor" d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6Z" />
                </Icon>
              </Flex>
            )}
            {onPlayToggle && (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                bg="blackAlpha.500"
                opacity={isPlayingInFloating ? 1 : 0}
                _groupHover={{ opacity: 1 }}
                transition="opacity 0.15s"
                borderRadius="8px"
              >
                <Icon viewBox="0 0 24 24" w={5} h={5} color="white">
                  {isPlayingInFloating
                    ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor" />
                    : <path d="M8 5v14l11-7L8 5z" fill="currentColor" />}
                </Icon>
              </Flex>
            )}
          </Box>
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="500" noOfLines={1}>{track.title ?? '-'}</Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>{track.artist ?? '-'}</Text>
          </Box>
        </Flex>
      </Td>

      {/* Genre */}
      <Td>
        <Text fontSize="sm" color="gray.600" noOfLines={1}>{track.genre ?? '—'}</Text>
      </Td>

      {/* Duration */}
      <Td isNumeric>
        <Text fontSize="sm" color="gray.600">
          {formatDuration(track.durationSeconds)}
        </Text>
      </Td>

      {/* Actions: drag handle + edit + remove */}
      <Td w="110px" px={2}>
        <Flex align="center" gap={1} justify="flex-end">
          <Box
            {...attributes}
            {...listeners}
            cursor="grab"
            p={1}
            display="flex"
            alignItems="center"
            data-testid={`drag-handle-${track.id}`}
            aria-label={t('admin.playlists.dragToReorder', 'Drag to reorder')}
          >
            <Icon viewBox="0 0 16 16" w={4} h={4} color="gray.400">
              <path fill="currentColor" d="M4 4h2v2H4V4zm6 0h2v2h-2V4zM4 7h2v2H4V7zm6 0h2v2h-2V7zm-6 3h2v2H4v-2zm6 0h2v2h-2v-2z" />
            </Icon>
          </Box>
          {onEdit && !isOverlay && (
            <IconButton
              aria-label={t('common.edit', 'Edit')}
              icon={
                <Icon viewBox="0 0 24 24" w={4} h={4}>
                  <path
                    fill="currentColor"
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                  />
                </Icon>
              }
              size="sm"
              variant="ghost"
              onClick={() => onEdit(track)}
              data-testid={`btn-edit-track-${track.id}`}
            />
          )}
          <IconButton
            aria-label={t('common.delete', 'Remove')}
            icon={
              <Icon viewBox="0 0 16 16" w={4} h={4}>
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </Icon>
            }
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => onRemove(track.trackId)}
            data-testid={`btn-remove-track-${track.id}`}
          />
        </Flex>
      </Td>
    </Tr>
  );
}
